import React, { useState } from 'react';
import { Plus, X, Edit, Trash2, Eye, EyeOff, CheckCircle, Upload, FileIcon, Image } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { createProduct, updateProduct, deleteProduct, toggleProductPublish, uploadEbook, uploadImage } from '../../utils/api';

export const AdminProducts = ({ products, loadData }) => {
  const [editingProduct, setEditingProduct] = useState(null);
  const [productFilter, setProductFilter] = useState('all');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', category: 'ebook', image_url: '', file_url: '', stock: '100'
  });

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      await createProduct({ ...productForm, price: parseFloat(productForm.price), stock: parseInt(productForm.stock) });
      toast.success('Product created successfully!');
      setProductForm({ name: '', description: '', price: '', category: 'ebook', image_url: '', file_url: '', stock: '100' });
      loadData();
    } catch (error) { toast.error('Failed to create product'); }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    if (!editingProduct) return;
    try {
      await updateProduct(editingProduct.id, { ...productForm, price: parseFloat(productForm.price), stock: parseInt(productForm.stock) });
      toast.success('Product updated successfully!');
      setEditingProduct(null);
      setProductForm({ name: '', description: '', price: '', category: 'ebook', image_url: '', file_url: '', stock: '100' });
      loadData();
    } catch (error) { toast.error('Failed to update product'); }
  };

  const handleEbookUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await uploadEbook(formData);
      setProductForm({ ...productForm, file_url: response.data.file_url });
      toast.success(`File "${response.data.filename}" uploaded successfully!`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to upload file'); }
    finally { setUploadingFile(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await uploadImage(formData);
      setProductForm({ ...productForm, image_url: response.data.file_url });
      toast.success(`Image "${response.data.filename}" uploaded successfully!`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to upload image'); }
    finally { setUploadingImage(false); }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try { await deleteProduct(productId); toast.success('Product deleted'); loadData(); }
    catch (error) { toast.error('Failed to delete product'); }
  };

  const handleToggleProductPublish = async (productId) => {
    try { const res = await toggleProductPublish(productId); toast.success(res.data.message); loadData(); }
    catch (error) { toast.error('Failed to update product status'); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="products-tab-content">
      <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-slate-900">{editingProduct ? 'Edit Product' : 'Create Product'}</h2>
          {editingProduct && (
            <Button type="button" variant="outline" size="sm" onClick={() => { setEditingProduct(null); setProductForm({ name: '', description: '', price: '', category: 'ebook', image_url: '', file_url: '', stock: '100' }); }}>
              <X className="mr-1 h-4 w-4" /> Cancel Edit
            </Button>
          )}
        </div>
        <form onSubmit={editingProduct ? handleUpdateProduct : handleCreateProduct} className="space-y-4">
          <div><Label htmlFor="product-name">Product Name</Label><Input id="product-name" value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} required className="mt-2" data-testid="product-name-input" /></div>
          <div><Label htmlFor="product-description">Description</Label><Textarea id="product-description" value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} required className="mt-2" rows={3} data-testid="product-description-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label htmlFor="product-price">Price (&#8369;)</Label><Input id="product-price" type="number" step="0.01" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} required className="mt-2" data-testid="product-price-input" /></div>
            {productForm.category === 'souvenir' && (<div><Label htmlFor="product-stock">Stock</Label><Input id="product-stock" type="number" value={productForm.stock} onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })} required className="mt-2" data-testid="product-stock-input" /></div>)}
          </div>
          <div><Label htmlFor="product-category">Category</Label>
            <select id="product-category" value={productForm.category} onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} className="mt-2 w-full h-10 rounded-lg border border-slate-200 px-3" data-testid="product-category-select">
              <option value="ebook">eBook</option><option value="souvenir">Souvenir</option>
            </select></div>
          <div><Label>Product Image</Label>
            <div className="mt-2 space-y-2">
              <label className="flex-1 cursor-pointer block">
                <div className={`border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-[#8B1818] transition-colors ${uploadingImage ? 'opacity-50' : ''}`}>
                  <Image className="h-6 w-6 mx-auto text-slate-400 mb-2" /><span className="text-sm text-slate-600">{uploadingImage ? 'Uploading...' : 'Click to upload image'}</span>
                  <input type="file" accept=".jpg,.jpeg,.png,.gif,.webp" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} />
                </div>
              </label>
              {productForm.image_url && (<div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg"><CheckCircle className="h-4 w-4 text-green-600" /><span className="text-sm text-green-800 truncate flex-1">Image uploaded</span><button type="button" onClick={() => setProductForm({ ...productForm, image_url: '' })} className="text-red-500 hover:text-red-700"><X className="h-4 w-4" /></button></div>)}
              <p className="text-xs text-slate-500">Or enter URL:</p>
              <Input type="url" placeholder="https://example.com/image.jpg" value={productForm.image_url} onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })} />
            </div></div>
          {productForm.category === 'ebook' && (<div><Label>eBook File (PDF, EPUB, MOBI)</Label>
            <div className="mt-2 space-y-2">
              <label className="cursor-pointer block">
                <div className={`border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-[#8B1818] transition-colors ${uploadingFile ? 'opacity-50' : ''}`}>
                  <Upload className="h-6 w-6 mx-auto text-slate-400 mb-2" /><span className="text-sm text-slate-600">{uploadingFile ? 'Uploading...' : 'Click to upload eBook file'}</span><p className="text-xs text-slate-400 mt-1">PDF, EPUB, MOBI, DOC, DOCX (max 50MB)</p>
                  <input type="file" accept=".pdf,.epub,.mobi,.doc,.docx" onChange={handleEbookUpload} className="hidden" disabled={uploadingFile} />
                </div>
              </label>
              {productForm.file_url && (<div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg"><FileIcon className="h-4 w-4 text-green-600" /><span className="text-sm text-green-800 truncate flex-1">eBook file uploaded</span><button type="button" onClick={() => setProductForm({ ...productForm, file_url: '' })} className="text-red-500 hover:text-red-700"><X className="h-4 w-4" /></button></div>)}
            </div></div>)}
          <Button type="submit" className="w-full bg-[#8B1818] hover:bg-[#701313] text-white" data-testid="create-product-button">
            {editingProduct ? (<><Edit className="mr-2 h-4 w-4" />Update Product</>) : (<><Plus className="mr-2 h-4 w-4" />Create Product</>)}
          </Button>
        </form>
      </div>

      <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-slate-900">All Products</h2>
          <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="all">All Categories</option><option value="ebook">eBooks Only</option><option value="souvenir">Souvenirs Only</option>
          </select>
        </div>
        <div className="space-y-4 max-h-[600px] overflow-y-auto">
          {products.filter(p => productFilter === 'all' || p.category === productFilter).length === 0 ? (
            <p className="text-slate-600 text-center py-8">No products found.</p>
          ) : (
            products.filter(p => productFilter === 'all' || p.category === productFilter).map((product) => (
              <div key={product.id} className={`p-4 border rounded-lg transition-colors ${product.is_published === false ? 'border-yellow-300 bg-yellow-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`product-item-${product.id}`}>
                <div className="flex justify-between items-start">
                  <div className="flex gap-4 flex-1">
                    {product.image_url && <img src={product.image_url} alt={product.name} className="w-16 h-16 object-cover rounded-lg" />}
                    <div className="flex-1">
                      <div className="flex items-center gap-2"><h3 className="font-bold text-slate-900">{product.name}</h3>{product.is_published === false && <span className="px-2 py-0.5 bg-yellow-200 text-yellow-800 rounded text-xs">Unpublished</span>}</div>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{product.description}</p>
                      <div className="mt-2 flex items-center flex-wrap gap-2 text-sm">
                        <span className="font-semibold text-[#8B1818]">&#8369;{product.price?.toFixed(2)}</span>
                        {product.category === 'souvenir' && <span className="text-slate-500">Stock: {product.stock}</span>}
                        <span className={`px-2 py-1 rounded text-xs ${product.category === 'ebook' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>{product.category}</span>
                        {product.file_url && <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs flex items-center gap-1"><FileIcon className="h-3 w-3" />File attached</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => { setEditingProduct(product); setProductForm({ name: product.name, description: product.description, price: product.price?.toString() || '', category: product.category, image_url: product.image_url || '', file_url: product.file_url || '', stock: product.stock?.toString() || '100' }); }} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg" title="Edit product"><Edit className="h-4 w-4" /></button>
                    <button onClick={() => handleToggleProductPublish(product.id)} className={`p-2 rounded-lg ${product.is_published === false ? 'text-green-500 hover:bg-green-50' : 'text-yellow-500 hover:bg-yellow-50'}`} title={product.is_published === false ? 'Publish' : 'Unpublish'}>{product.is_published === false ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
                    <button onClick={() => handleDeleteProduct(product.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Delete product"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
